import React, { useState, useEffect } from 'react';
import {
  Table,
  Button,
  Modal,
  Form,
  InputNumber,
  Select,
  TimePicker,
  Switch,
  message,
  Space,
  Popconfirm,
  Tag,
  Card,
  Row,
  Col,
  Checkbox,
  Divider,
  Alert,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ReloadOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { apiClient } from '../../../config/apiClient';
import MainLayout from '../../layout/layout';

const PreciosClub = () => {
  const [franjas, setFranjas] = useState([]);
  const [canchas, setCanchas] = useState([]);
  const [canchaSeleccionada, setCanchaSeleccionada] = useState(null);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [modalMasivoVisible, setModalMasivoVisible] = useState(false);
  const [editingFranja, setEditingFranja] = useState(null);
  const [form] = Form.useForm();
  const [formMasivo] = Form.useForm();
  const [loadingMasivo, setLoadingMasivo] = useState(false);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768);

  const diasSemana = [
    { value: 0, label: 'Domingo' },
    { value: 1, label: 'Lunes' },
    { value: 2, label: 'Martes' },
    { value: 3, label: 'Miércoles' },
    { value: 4, label: 'Jueves' },
    { value: 5, label: 'Viernes' },
    { value: 6, label: 'Sábado' },
  ];

  // Cargar canchas activas
  const loadCanchas = async () => {
    try {
      const response = await apiClient.get('/club/canchas/activas');
      if (response.ok) {
        const data = await response.json();
        setCanchas(data);
        if (data.length > 0 && !canchaSeleccionada) {
          setCanchaSeleccionada(data[0].ccl_id);
        }
      }
    } catch (error) {
      console.error('Error al cargar canchas:', error);
    }
  };

  // Cargar franjas horarias
  const loadFranjas = async () => {
    if (!canchaSeleccionada) return;
    
    setLoading(true);
    try {
      const response = await apiClient.get(`/club/franjas-horarias/cancha/${canchaSeleccionada}`);
      if (response.ok) {
        const data = await response.json();
        setFranjas(data);
      } else {
        message.error('Error al cargar las franjas horarias');
      }
    } catch (error) {
      console.error('Error:', error);
      message.error('Error al cargar las franjas horarias');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCanchas();
  }, []);

  useEffect(() => {
    loadFranjas();
  }, [canchaSeleccionada]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Abrir modal para crear nueva franja
  const handleCreate = () => {
    if (!canchaSeleccionada) {
      message.warning('Por favor seleccione una cancha primero');
      return;
    }
    setEditingFranja(null);
    form.resetFields();
    form.setFieldsValue({ activa: true });
    setModalVisible(true);
  };

  // Abrir modal para crear múltiples franjas
  const handleCreateMasivo = () => {
    if (canchas.length === 0) {
      message.warning('No hay canchas disponibles');
      return;
    }
    formMasivo.resetFields();
    formMasivo.setFieldsValue({ activa: true });
    setModalMasivoVisible(true);
  };

  // Guardar múltiples franjas
  const handleSaveMasivo = async () => {
    try {
      const values = await formMasivo.validateFields();
      const horaDesde = values.horaDesde.format('HH:mm:ss');
      const horaHasta = values.horaHasta.format('HH:mm:ss');
      const canchasSeleccionadas = values.canchas || [];
      const diasSeleccionados = values.dias || [];

      if (canchasSeleccionadas.length === 0) {
        message.warning('Debe seleccionar al menos una cancha');
        return;
      }

      if (diasSeleccionados.length === 0) {
        message.warning('Debe seleccionar al menos un día');
        return;
      }

      setLoadingMasivo(true);

      // Crear todas las combinaciones
      const combinaciones = [];
      canchasSeleccionadas.forEach(canchaId => {
        diasSeleccionados.forEach(dia => {
          combinaciones.push({
            canchaClub: canchaId,
            diaSemana: dia,
            horaDesde,
            horaHasta,
            precio: values.precio,
            activa: values.activa,
          });
        });
      });

      // Enviar todas las combinaciones
      let exitosas = 0;
      let errores = 0;

      for (const data of combinaciones) {
        try {
          const response = await apiClient.post('/club/franjas-horarias', data);
          if (response.ok) {
            exitosas++;
          } else {
            errores++;
          }
        } catch (error) {
          errores++;
        }
      }

      if (errores === 0) {
        message.success(`Se crearon ${exitosas} franjas horarias correctamente`);
      } else if (exitosas > 0) {
        message.warning(`Se crearon ${exitosas} franjas horarias. ${errores} fallaron (posiblemente ya existían)`);
      } else {
        message.error('Error al crear las franjas horarias. Puede que ya existan algunas combinaciones.');
      }

      setModalMasivoVisible(false);
      
      // Recargar franjas si la cancha seleccionada está en las creadas
      if (canchaSeleccionada && canchasSeleccionadas.includes(canchaSeleccionada)) {
        loadFranjas();
      }
    } catch (error) {
      console.error('Error:', error);
      if (error.errorFields) {
        return;
      }
      message.error('Error al guardar las franjas horarias');
    } finally {
      setLoadingMasivo(false);
    }
  };

  // Abrir modal para editar franja
  const handleEdit = (record) => {
    setEditingFranja(record);
    // Convertir precio a número si viene como string
    const precioNum = typeof record.fho_precio === 'string' 
      ? parseFloat(record.fho_precio) 
      : record.fho_precio;
    
    form.setFieldsValue({
      diaSemana: record.fho_dia_semana,
      horaDesde: dayjs(record.fho_hora_desde, 'HH:mm:ss'),
      horaHasta: dayjs(record.fho_hora_hasta, 'HH:mm:ss'),
      precio: precioNum || 0,
      activa: record.fho_activa,
    });
    setModalVisible(true);
  };

  // Guardar franja (crear o actualizar)
  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      const horaDesde = values.horaDesde.format('HH:mm:ss');
      const horaHasta = values.horaHasta.format('HH:mm:ss');
      
      const data = {
        canchaClub: canchaSeleccionada,
        diaSemana: values.diaSemana,
        horaDesde,
        horaHasta,
        precio: values.precio,
        activa: values.activa,
      };

      if (editingFranja) {
        // Actualizar
        const response = await apiClient.put(`/club/franjas-horarias/${editingFranja.fho_id}`, data);
        if (response.ok) {
          message.success('Franja horaria actualizada correctamente');
          setModalVisible(false);
          loadFranjas();
        } else {
          const error = await response.json();
          message.error(error.message || 'Error al actualizar la franja horaria');
        }
      } else {
        // Crear
        const response = await apiClient.post('/club/franjas-horarias', data);
        if (response.ok) {
          message.success('Franja horaria creada correctamente');
          setModalVisible(false);
          loadFranjas();
        } else {
          const error = await response.json();
          message.error(error.message || 'Error al crear la franja horaria');
        }
      }
    } catch (error) {
      console.error('Error:', error);
      if (error.errorFields) {
        return;
      }
      message.error('Error al guardar la franja horaria');
    }
  };

  // Eliminar franja
  const handleDelete = async (id) => {
    try {
      const response = await apiClient.delete(`/club/franjas-horarias/${id}`);
      if (response.ok) {
        message.success('Franja horaria eliminada correctamente');
        loadFranjas();
      } else {
        const error = await response.json();
        message.error(error.message || 'Error al eliminar la franja horaria');
      }
    } catch (error) {
      console.error('Error:', error);
      message.error('Error al eliminar la franja horaria');
    }
  };

  // Columnas de la tabla
  const columns = [
    {
      title: 'Día',
      dataIndex: 'fho_dia_semana',
      key: 'fho_dia_semana',
      width: 120,
      render: (dia) => {
        const diaObj = diasSemana.find(d => d.value === dia);
        return diaObj ? diaObj.label : `Día ${dia}`;
      },
    },
    {
      title: 'Hora Desde',
      dataIndex: 'fho_hora_desde',
      key: 'fho_hora_desde',
      width: 120,
      render: (hora) => hora.substring(0, 5),
    },
    {
      title: 'Hora Hasta',
      dataIndex: 'fho_hora_hasta',
      key: 'fho_hora_hasta',
      width: 120,
      render: (hora) => hora.substring(0, 5),
    },
    {
      title: 'Precio',
      dataIndex: 'fho_precio',
      key: 'fho_precio',
      width: 120,
      render: (precio) => {
        const precioNum = typeof precio === 'string' ? parseFloat(precio) : precio;
        return `$${precioNum ? precioNum.toFixed(2) : '0.00'}`;
      },
    },
    {
      title: 'Estado',
      dataIndex: 'fho_activa',
      key: 'fho_activa',
      width: 100,
      render: (activa) => (
        <Tag color={activa ? 'green' : 'red'}>
          {activa ? 'Activa' : 'Inactiva'}
        </Tag>
      ),
    },
    {
      title: 'Acciones',
      key: 'acciones',
      width: 150,
      render: (_, record) => (
        <Space size="small">
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            size="small"
          >
            Editar
          </Button>
          <Popconfirm
            title="¿Está seguro de eliminar esta franja horaria?"
            onConfirm={() => handleDelete(record.fho_id)}
            okText="Sí"
            cancelText="No"
          >
            <Button
              type="link"
              danger
              icon={<DeleteOutlined />}
              size="small"
            >
              Eliminar
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <MainLayout>
      <Card
        title="Configuración de Precios por Franja Horaria"
        extra={
          <Space>
            <Button
              icon={<ReloadOutlined />}
              onClick={loadFranjas}
              loading={loading}
            >
              Actualizar
            </Button>
            <Button
              icon={<ThunderboltOutlined />}
              onClick={handleCreateMasivo}
              style={{ background: '#52c41a', borderColor: '#52c41a', color: '#fff' }}
            >
              Crear Múltiples
            </Button>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleCreate}
              disabled={!canchaSeleccionada}
            >
              Nueva Franja
            </Button>
          </Space>
        }
      >
        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col xs={24} sm={12} md={8}>
            <Select
              placeholder="Seleccione una cancha"
              value={canchaSeleccionada}
              onChange={setCanchaSeleccionada}
              style={{ width: '100%' }}
            >
              {canchas.map((cancha) => (
                <Select.Option key={cancha.ccl_id} value={cancha.ccl_id}>
                  {cancha.ccl_nombre} - {cancha.tipo_deporte_nombre}
                </Select.Option>
              ))}
            </Select>
          </Col>
        </Row>

        {canchaSeleccionada && (
          <Table
            columns={columns}
            dataSource={franjas}
            loading={loading}
            rowKey="fho_id"
            scroll={{ x: isMobile ? 800 : 'auto' }}
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showTotal: (total) => `Total: ${total} franjas`,
            }}
          />
        )}

        {/* Modal para crear/editar franja individual */}
        <Modal
          title={editingFranja ? 'Editar Franja Horaria' : 'Nueva Franja Horaria'}
          open={modalVisible}
          onOk={handleSave}
          onCancel={() => setModalVisible(false)}
          okText="Guardar"
          cancelText="Cancelar"
          width={isMobile ? '90%' : 600}
        >
          <Form
            form={form}
            layout="vertical"
            initialValues={{ activa: true }}
          >
            <Form.Item
              name="diaSemana"
              label="Día de la Semana"
              rules={[{ required: true, message: 'El día de la semana es requerido' }]}
            >
              <Select placeholder="Seleccione un día">
                {diasSemana.map((dia) => (
                  <Select.Option key={dia.value} value={dia.value}>
                    {dia.label}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              name="horaDesde"
              label="Hora Desde"
              rules={[{ required: true, message: 'La hora de inicio es requerida' }]}
            >
              <TimePicker format="HH:mm" style={{ width: '100%' }} />
            </Form.Item>

            <Form.Item
              name="horaHasta"
              label="Hora Hasta"
              rules={[{ required: true, message: 'La hora de fin es requerida' }]}
            >
              <TimePicker format="HH:mm" style={{ width: '100%' }} />
            </Form.Item>

            <Form.Item
              name="precio"
              label="Precio"
              rules={[
                { required: true, message: 'El precio es requerido' },
                {
                  validator: (_, value) => {
                    if (value === null || value === undefined || value === '') {
                      return Promise.reject(new Error('El precio es requerido'));
                    }
                    const precioNum = typeof value === 'string' ? parseFloat(value) : value;
                    if (isNaN(precioNum)) {
                      return Promise.reject(new Error('El precio debe ser un número válido'));
                    }
                    if (precioNum < 0) {
                      return Promise.reject(new Error('El precio debe ser mayor o igual a cero'));
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            >
              <InputNumber
                prefix="$"
                style={{ width: '100%' }}
                min={0}
                step={0.01}
                precision={2}
              />
            </Form.Item>

            <Form.Item
              name="activa"
              label="Activa"
              valuePropName="checked"
            >
              <Switch />
            </Form.Item>
          </Form>
        </Modal>

        {/* Modal para crear múltiples franjas */}
        <Modal
          title="Crear Múltiples Franjas Horarias"
          open={modalMasivoVisible}
          onOk={handleSaveMasivo}
          onCancel={() => setModalMasivoVisible(false)}
          okText="Crear Todas"
          cancelText="Cancelar"
          width={isMobile ? '90%' : 700}
          confirmLoading={loadingMasivo}
        >
          <Alert
            message="Carga Rápida"
            description="Seleccione múltiples canchas y días para aplicar el mismo precio y horario a todas las combinaciones."
            type="info"
            showIcon
            style={{ marginBottom: 24 }}
          />
          
          <Form
            form={formMasivo}
            layout="vertical"
            initialValues={{ activa: true }}
          >
            <Form.Item
              name="canchas"
              label="Canchas"
              rules={[{ required: true, message: 'Debe seleccionar al menos una cancha' }]}
            >
              <Select
                mode="multiple"
                placeholder="Seleccione una o más canchas"
                style={{ width: '100%' }}
                showSearch
                optionFilterProp="children"
                filterOption={(input, option) =>
                  (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                }
              >
                {canchas.map((cancha) => (
                  <Select.Option 
                    key={cancha.ccl_id} 
                    value={cancha.ccl_id}
                    label={`${cancha.ccl_nombre} - ${cancha.tipo_deporte_nombre}`}
                  >
                    {cancha.ccl_nombre} - {cancha.tipo_deporte_nombre}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              name="dias"
              label="Días de la Semana"
              rules={[{ required: true, message: 'Debe seleccionar al menos un día' }]}
            >
              <Select
                mode="multiple"
                placeholder="Seleccione uno o más días"
                style={{ width: '100%' }}
              >
                {diasSemana.map((dia) => (
                  <Select.Option key={dia.value} value={dia.value}>
                    {dia.label}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>

            <Divider />

            <Form.Item
              name="horaDesde"
              label="Hora Desde"
              rules={[{ required: true, message: 'La hora de inicio es requerida' }]}
            >
              <TimePicker format="HH:mm" style={{ width: '100%' }} />
            </Form.Item>

            <Form.Item
              name="horaHasta"
              label="Hora Hasta"
              rules={[{ required: true, message: 'La hora de fin es requerida' }]}
            >
              <TimePicker format="HH:mm" style={{ width: '100%' }} />
            </Form.Item>

            <Form.Item
              name="precio"
              label="Precio"
              rules={[
                { required: true, message: 'El precio es requerido' },
                {
                  validator: (_, value) => {
                    if (value === null || value === undefined || value === '') {
                      return Promise.reject(new Error('El precio es requerido'));
                    }
                    const precioNum = typeof value === 'string' ? parseFloat(value) : value;
                    if (isNaN(precioNum)) {
                      return Promise.reject(new Error('El precio debe ser un número válido'));
                    }
                    if (precioNum < 0) {
                      return Promise.reject(new Error('El precio debe ser mayor o igual a cero'));
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            >
              <InputNumber
                prefix="$"
                style={{ width: '100%' }}
                min={0}
                step={0.01}
                precision={2}
              />
            </Form.Item>

            <Form.Item
              name="activa"
              label="Estado"
              valuePropName="checked"
            >
              <Switch checkedChildren="Activa" unCheckedChildren="Inactiva" />
            </Form.Item>

            <Form.Item
              noStyle
              shouldUpdate={(prevValues, currentValues) => 
                prevValues.canchas !== currentValues.canchas || 
                prevValues.dias !== currentValues.dias
              }
            >
              {({ getFieldValue }) => {
                const canchasSeleccionadas = getFieldValue('canchas') || [];
                const diasSeleccionados = getFieldValue('dias') || [];
                const totalCombinaciones = canchasSeleccionadas.length * diasSeleccionados.length;
                
                if (totalCombinaciones > 0) {
                  return (
                    <Alert
                      message={`Se crearán ${totalCombinaciones} franja${totalCombinaciones !== 1 ? 's' : ''} horaria${totalCombinaciones !== 1 ? 's' : ''}`}
                      description={`${canchasSeleccionadas.length} cancha${canchasSeleccionadas.length !== 1 ? 's' : ''} × ${diasSeleccionados.length} día${diasSeleccionados.length !== 1 ? 's' : ''} = ${totalCombinaciones} combinación${totalCombinaciones !== 1 ? 'es' : ''}`}
                      type="success"
                      showIcon
                      style={{ marginTop: 16 }}
                    />
                  );
                }
                return null;
              }}
            </Form.Item>
          </Form>
        </Modal>
      </Card>
    </MainLayout>
  );
};

export default PreciosClub;
